import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "@mui/material";
import { Box } from "@mui/system";
import HighchartsReact from "highcharts-react-official";
import Highcharts, { ColorType, YAxisOptions } from "highcharts/highstock";
import { DateTime } from "luxon";

import { DATETIME_FORMAT_DEFAULT } from "@config";
import { formatNumberForLocale, numbersOnly } from "@core";

import type { Resolution } from "../shared/types";

export type SignatureDataPoint = {
  outdoor: number;
  value: number;
  timestamp: number;
  color?: string;
};

export type SignatureSeriesData = {
  uid: string;
  name: string;
  data: SignatureDataPoint[];
  unit: string;
  color?: ColorType;
  yAxisIndex?: number;
  visible?: boolean;
};

export type SignatureYAxisConfig = YAxisOptions;

export type BaseSignatureDiagramChartProps = {
  series: SignatureSeriesData[];
  yAxisConfigs: SignatureYAxisConfig[];

  chartHeight?: number;
  chartId?: string;
  resolution?: Resolution;

  onSeriesToggle?: (seriesUid: string) => void;
  allowSeriesToggle?: boolean;

  isLoading?: boolean;
  yAxisLayout?: "stacked" | "side-by-side";
};

const CHART_DEFAULTS = {
  DEFAULT_HEIGHT: 550,
};

// Highcharts-compatible styles mapped to semantic typography variants
const CHART_TYPOGRAPHY = {
  labelLarge: { fontSize: "14px", fontWeight: "500" },
  labelMedium: { fontSize: "12px", fontWeight: "500" },
  bodySmall: { fontSize: "12px", fontWeight: "300" },
};

/**
 * BaseSignatureDiagramChart - Reusable scatter plot against outdoor temperature
 *
 * Follows the same container pattern as BaseTimeSeriesChart but for signature diagrams
 * (scatter plots where X-axis is outdoor temperature, not time).
 */
export const BaseSignatureDiagramChart: React.FC<BaseSignatureDiagramChartProps> = ({
  series,
  yAxisConfigs,
  chartHeight = CHART_DEFAULTS.DEFAULT_HEIGHT,
  chartId = "signature",
  resolution = "hourly",
  onSeriesToggle,
  allowSeriesToggle = true,
  isLoading = false,
  yAxisLayout = "side-by-side",
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Track series visibility
  const [seriesVisibility, setSeriesVisibility] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    series.forEach((s) => {
      initial[s.uid] = s.visible !== false;
    });
    return initial;
  });

  // Update visibility when series prop changes
  useEffect(() => {
    setSeriesVisibility((prev) => {
      const updated = { ...prev };
      series.forEach((s) => {
        if (!(s.uid in updated)) {
          updated[s.uid] = s.visible !== false;
        }
      });
      return updated;
    });
  }, [series]);

  const handleSeriesToggle = useCallback(
    (seriesUid: string) => {
      // Prevent hiding all series
      const visibleCount = Object.values(seriesVisibility).filter(Boolean).length;
      if (visibleCount === 1 && seriesVisibility[seriesUid]) {
        return;
      }

      setSeriesVisibility((prev) => ({
        ...prev,
        [seriesUid]: !prev[seriesUid],
      }));

      onSeriesToggle?.(seriesUid);
    },
    [seriesVisibility, onSeriesToggle]
  );

  // Chart options
  const chartOptions = useMemo((): Highcharts.Options => {
    // Calculate x-axis range with 5% padding
    const allData = series.filter((s) => seriesVisibility[s.uid]).flatMap((s) => s.data);
    const xValues = allData.map((d) => d.outdoor).filter(numbersOnly);

    let xMin: number | undefined;
    let xMax: number | undefined;
    if (xValues.length > 0) {
      const rawMax = Math.max(...xValues);
      const rawMin = Math.min(...xValues);
      xMax = rawMax + (Math.abs(rawMax) / 100) * 5;
      xMin = rawMin - (Math.abs(rawMin) / 100) * 5;
    }

    // Build y-axis
    const visibleYAxes = new Set<number>();
    series.forEach((s) => {
      if (seriesVisibility[s.uid]) {
        visibleYAxes.add(s.yAxisIndex || 0);
      }
    });

    const yAxis: Highcharts.YAxisOptions[] = [];

    if (yAxisLayout === "side-by-side") {
      yAxisConfigs.forEach((config) => {
        const { title, labels, ...rest } = config;
        yAxis.push({
          labels: { style: CHART_TYPOGRAPHY.bodySmall, ...labels },
          title: { style: CHART_TYPOGRAPHY.labelMedium, ...title },
          lineWidth: 1,
          gridLineWidth: 0,
          opposite: false,
          showEmpty: false,
          ...rest,
        });
      });
    } else {
      // Stacked layout — percentage-based (matches AverageIndoorTempXHumidGraph)
      const visibleAxes = yAxisConfigs.map((_, i) => i).filter((i) => visibleYAxes.has(i));
      const visibleAxisCount = Math.max(visibleAxes.length, 1);
      const GAP_PERCENT = 8;
      const totalGap = visibleAxisCount > 1 ? (visibleAxisCount - 1) * GAP_PERCENT : 0;
      const heightPercent = (100 - totalGap) / visibleAxisCount;

      yAxisConfigs.forEach((config, index) => {
        const stackIdx = visibleAxes.indexOf(index);
        const isVisible = stackIdx !== -1;
        const { title, labels, ...rest } = config;

        yAxis.push({
          visible: isVisible,
          labels: { style: CHART_TYPOGRAPHY.bodySmall, ...labels },
          title: { style: CHART_TYPOGRAPHY.labelMedium, ...title },
          lineWidth: 1,
          gridLineWidth: 0,
          opposite: false,
          top: isVisible ? `${stackIdx * (heightPercent + GAP_PERCENT)}%` : "0%",
          height: isVisible ? `${heightPercent}%` : "0%",
          offset: 0,
          ...rest,
        });
      });

      // Add a hidden axis spanning the full height to draw a continuous y-axis line
      if (visibleAxisCount > 1) {
        yAxis.push({
          top: "0%",
          height: "100%",
          lineWidth: 1,
          gridLineWidth: 0,
          labels: { enabled: false },
          title: { text: undefined },
          tickLength: 0,
          min: 0,
          max: 1,
          visible: true,
          offset: 0,
        });
      }
    }

    // Build series
    const chartSeries: Highcharts.SeriesOptionsType[] = series.map((s) => {
      const yAxisIndex = s.yAxisIndex || 0;

      return {
        id: s.uid,
        name: s.name,
        type: "scatter" as const,
        data: s.data.map((point) => ({
          x: point.outdoor,
          y: point.value,
          z: point.timestamp,
          color: point.color,
        })),
        tooltip: { valueSuffix: ` ${s.unit}` },
        color: s.color,
        yAxis: yAxisIndex,
        visible: seriesVisibility[s.uid],
        showInLegend: true,
        events: allowSeriesToggle
          ? {
              legendItemClick: function (e: Highcharts.SeriesLegendItemClickEventObject) {
                const seriesId = (this as any).options.id as string;
                handleSeriesToggle(seriesId);
                return false;
              },
            }
          : undefined,
      };
    });

    // Tooltip date format based on resolution
    const dateFormat = resolution === "daily" ? "yyyy-MM-dd" : DATETIME_FORMAT_DEFAULT;

    return {
      chart: {
        height: chartHeight,
        type: "scatter",
        animation: false,
        spacing: [10, 30, 15, 10],
        // @ts-expect-error zoomType typing mismatch
        zoomType: "xy" as const,
        zooming: {
          mouseWheel: { enabled: false },
        },
        resetZoomButton: {
          position: { align: "right", verticalAlign: "top", x: -10, y: 10 },
        },
      },
      title: undefined,
      xAxis: {
        title: { text: "Outdoor Temperature (°C)", style: CHART_TYPOGRAPHY.labelMedium },
        labels: { style: CHART_TYPOGRAPHY.bodySmall },
        gridLineWidth: 0,
        lineWidth: 1,
        min: xMin,
        max: xMax,
      },
      yAxis,
      legend: {
        enabled: true,
        align: "left",
        verticalAlign: "top",
        layout: "horizontal",
        symbolWidth: 16,
        symbolHeight: 12,
        useHTML: false,
        margin: 20,
        itemStyle: CHART_TYPOGRAPHY.labelLarge,
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: 3,
            symbol: "circle",
          },
        },
      },
      series: chartSeries,
      tooltip: {
        animation: false,
        formatter() {
          const { series: chartSeries, point, y, x } = this as any;
          const { z } = point;
          const formattedDate = DateTime.fromMillis(z).toFormat(dateFormat);
          return `<b>${chartSeries.name} ${formatNumberForLocale(y)}${
            chartSeries.tooltipOptions.valueSuffix
          } / ${formatNumberForLocale(x)}°C <br/>${formattedDate}</b>`;
        },
      },
      credits: { enabled: false },
      navigation: {
        buttonOptions: {
          enabled: chartSeries.length > 0,
        },
      },
    };
  }, [
    series,
    yAxisConfigs,
    seriesVisibility,
    chartHeight,
    resolution,
    allowSeriesToggle,
    yAxisLayout,
    handleSeriesToggle,
    chartId,
  ]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: `${chartHeight}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="bodyMedium" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  const hasData = series.some((s) => s.data && s.data.length > 0);
  if (!hasData) {
    return (
      <Box
        sx={{
          height: `${chartHeight}px`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="bodyMedium" color="text.secondary">
          No data available for the selected period
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
        containerProps={{ style: { width: "100%", height: "100%" } }}
      />
    </Box>
  );
};
